export type FacilityId =
  | "restaurant"
  | "bar"
  | "room_service"
  | "spa"
  | "parking"
  | "meetings"
  | "pool"
  | "gym";

export const FACILITY_OPTIONS: { id: FacilityId; label: string }[] = [
  { id: "restaurant", label: "Restaurant" },
  { id: "bar", label: "Bar" },
  { id: "room_service", label: "Room Service" },
  { id: "spa", label: "Spa" },
  { id: "parking", label: "Parking" },
  { id: "meetings", label: "Meeting & Events" },
  { id: "pool", label: "Pool" },
  { id: "gym", label: "Gym" },
];

export function labelToId(s: string): FacilityId | null {
  const v = String(s)
    .trim()
    .toLowerCase()
    .replace(/[\s_-]/g, "");
  if (!v) return null;
  if (["restaurant", "restaurants"].includes(v)) return "restaurant";
  if (["bar", "bars"].includes(v)) return "bar";
  if (["roomservice", "roomservice"].includes(v)) return "room_service";
  if (["spa", "wellness"].includes(v)) return "spa";
  if (["parking", "carpark"].includes(v)) return "parking";
  if (
    [
      "meeting",
      "meetings",
      "meetingevents",
      "meetingandevents",
      "events",
      "conference",
    ].includes(v)
  )
    return "meetings";
  if (["pool", "swimmingpool"].includes(v)) return "pool";
  if (["gym", "fitness", "fitnesscenter", "fitnesscentre"].includes(v))
    return "gym";
  return null;
}

export function normalizeFacilities(input: unknown): FacilityId[] {
  if (!Array.isArray(input)) return [];
  const out: FacilityId[] = [];
  for (const val of input) {
    const raw = String(val ?? "");
    const exact = FACILITY_OPTIONS.find((o) => o.id === raw);
    if (exact) {
      if (!out.includes(exact.id)) out.push(exact.id);
      continue;
    }
    const mapped = labelToId(raw);
    if (mapped && !out.includes(mapped)) out.push(mapped);
  }
  return out;
}

export function facilitiesToBooleans(
  ids: FacilityId[],
): Record<FacilityId, boolean> {
  const map: Record<FacilityId, boolean> = {
    restaurant: false,
    bar: false,
    room_service: false,
    spa: false,
    parking: false,
    meetings: false,
    pool: false,
    gym: false,
  };
  for (const id of ids) if (id in map) map[id] = true;
  return map;
}

export function booleansToFacilities(
  b: Partial<Record<FacilityId, boolean>>,
): FacilityId[] {
  return FACILITY_OPTIONS.map((o) => o.id).filter((id) => Boolean(b?.[id]));
}

export function facilityKey(raw: string): FacilityId | string {
  const exact = FACILITY_OPTIONS.find((o) => o.id === raw);
  if (exact) return exact.id;
  return labelToId(raw) ?? raw;
}

export function facilityLabel(raw: string): string {
  const exact = FACILITY_OPTIONS.find((o) => o.id === raw);
  if (exact) return exact.label;
  const id = labelToId(raw);
  if (id) return FACILITY_OPTIONS.find((o) => o.id === id)?.label ?? raw;
  return raw;
}

export function formatFacilityId(id: string): string {
  return id
    .split("_")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
}
